'use client'

import React, { useEffect, useRef, useState } from 'react'

import { Icon } from '@/app/components/Icon'
import { cn } from '@/app/utils/style'

export type DropdownItem = {
  value: string
  label: string
}

type CustomDropdownProps = {
  items: DropdownItem[]
  value?: string
  placeholder?: string
  onChange: (value: string) => void
  className?: string
}

export function CustomDropdown({
  items,
  value,
  placeholder = 'Select an option',
  onChange,
  className = '',
}: CustomDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const selectedItem = items.find((item) => item.value === value)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [])

  const handleSelect = (itemValue: string) => {
    onChange(itemValue)
    setIsOpen(false)
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      setIsOpen(!isOpen)
    }
  }

  return (
    <div ref={dropdownRef} className={cn('relative w-full', className)}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        className={cn(
          'w-full py-lg px-2xl cursor-pointer text-primary-600 size-body-sm font-medium outline-none bg-white flex items-center justify-between transition-[color,border,box-shadow]',
          isOpen ? 'rounded-t-xl' : 'rounded-xl',
          !isOpen &&
            '[box-shadow:inset_0_0_0_2px_theme(colors.primary.50)] hover:bg-gray-50 hover:[box-shadow:inset_0_0_0_2px_theme(colors.primary.200)]',
        )}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span>{selectedItem ? selectedItem.label : placeholder}</span>
        <Icon
          provider="fontawesome"
          name="faChevronDown"
          size={14}
          className={cn('transition-transform', isOpen && 'rotate-180')}
        />
      </button>

      <ul
        className={`absolute rounded-b-xl shadow-dropdown bg-white pb-sm z-[1000] min-w-full w-max overflow-auto ${isOpen ? '' : 'hidden'}`}
        role="listbox"
      >
        {items.map((item) => (
          <li
            key={item.value}
            onClick={() => handleSelect(item.value)}
            className={cn(
              'py-sm px-2xl size-body-sm font-medium cursor-pointer transition-[color,background]',
              'text-neutral-600 bg-white',
              'hover:text-neutral-900 hover:bg-primary-50',
              item.value === value && 'text-primary-600 hover:text-primary-600',
            )}
            role="option"
            aria-selected={item.value === value}
          >
            {item.label}
          </li>
        ))}
      </ul>
    </div>
  )
}
